import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetStaticProps } from 'next';
import { head, flatMap } from 'lodash';
import { TextDescriptionFade } from '@components/common/description/TextDescriptionFade';
import { Head } from '@components/common/head';
import { useI18NextContext } from '@components/hooks';
import { DestinationBreadcrumb } from '@components/breadcrumbs/destination';
import { getHero } from '@components/util';
import { getPlacePath } from '@model/iceberg/geography';
import { ComponentContainer, SectionSeparator } from '@styles/layout';
import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import { Footer } from '@components/footer';
import { ContentApi } from '@model/iceberg/service/content';
import { DestinationHero } from '@components/common/hero/destination/DestinationHero';
import { withGlobalProvider } from 'providers/globalProvider';
import { storeWrapper } from '@state/store';
import { initializeApp } from '@state/initializeApp';
import { PageProps } from '@model/pages';
import moment from 'moment';
import { setDestination } from '@state/slices/app';
import { ImagesResponse } from '@model/iceberg';
import { Page, Country, Region, Resort, TripTypes } from '@model/common';
import { ProductPackageGroup } from '@components/product/product-package/ProductPackageGroup';
import { HotelContentApi } from '@model/iceberg/service/hotel-content/hotel-content-api';
import { HotelTile } from '@model/iceberg/hotel-content/hotel-content';
import { Revalidate } from '@model/config/revalidate';
import { setPartialSearch } from '@state/slices/deal-finder/dealFinderSlice';
import { DestinationSearchParams, getDestinationSearchParams } from '@util/destination';
import { getAlgoliaParentDestinations } from '@state/slices/algolia/algoliaSelectors';
import DestinationFAQ from '@components/destination/DestinationFAQ';
import { DestinationFaqItem } from '@state/slices/destination-faq';
import { DestinationFaqMeta } from '@components/meta/DestinationFaqMeta';
import { getDestinationFaqData } from '@components/util/get-destination-faq';
import { HolidaysCallToBookContainer } from '@components/content/fit';
import { Error500 } from '@components/common/hero/error/Error500';
import { ERROR_CODES } from '@model/common/error-codes';
import { truncateGallery } from '@util/images';
import { findByPath, getCountryPath, getRegionPath } from '@util/path';
import { ResortLanding } from '@model/content';

/* ***************** *
 *       Types       *
 * ***************** */
export interface ResortPageProps extends PageProps {
  path: string;
  destination: Country | Region;
  region: Region | null;
  resort: Resort | null;
  resortLanding: ResortLanding | null;
  hero: ImagesResponse;
  popularHotels: Array<HotelTile>;
  destinationFaq: Array<DestinationFaqItem>;
}

/* ********************** *
 *       Resort Page      *
 * ********************** */

const ResortPage = ({
  path,
  destination,
  region,
  resort,
  resortLanding,
  hero,
  popularHotels,
  destinationFaq,
  promoBannerTripType
}: ResortPageProps) => {
  const dispatch = useDispatch();
  const t: any = useI18NextContext();
  const [hasPopulatedSearch, setHasPopulatedSearch] = useState(false);
  const algoliaDestinations = useSelector(getAlgoliaParentDestinations(path));

  if (!region || !resort) return <Error500 code={ERROR_CODES.NO_CMS_CONFIG} />;

  useEffect(() => {
    setHasPopulatedSearch(false);
  }, [path]);

  useEffect(() => {
    if (algoliaDestinations.length && !hasPopulatedSearch) {
      const { display, path } = resort;

      const defaultSearchParams = getDestinationSearchParams({
        display,
        path,
        destinations: algoliaDestinations
      } as DestinationSearchParams);
      dispatch(setPartialSearch(defaultSearchParams));
      setHasPopulatedSearch(true);
    }
  }, [algoliaDestinations, hasPopulatedSearch]);

  const date = moment();
  const from = date.year();
  const to = date.add(1, 'year').year();
  const metaDescription = t('seo__resort-page--description', {
    destination: destination.display,
    resort: resort.display
  });
  const metaTitle = t('seo__resort-page--title', {
    destination: destination.display,
    resort: resort.display,
    from,
    to
  });
  const canRenderDestinationFaq: boolean = !!destinationFaq.length;

  return (
    <>
      {canRenderDestinationFaq && <DestinationFaqMeta faqs={destinationFaq} />}
      <Head metaTitle={metaTitle} metaDescription={metaDescription} />
      <DestinationHero src={hero.images[0]} promoBannerTripType={promoBannerTripType} />
      <DestinationBreadcrumb destination={destination} resort={resort} tripType={TripTypes.HOLIDAYS} />
      <ComponentContainer>
        <SectionSeparator />
        <SectionTitle size={SectionTitleSizes.LARGE} heading={resort.display} main />
        <TextDescriptionFade description={resortLanding?.description || ''} initialHeight={150} />
        <SectionSeparator />
        <ProductPackageGroup
          path={path}
          destination={resort.display}
          packages={popularHotels}
          place={{
            region,
            resort
          }}
        />
        <SectionSeparator />
        <HolidaysCallToBookContainer destination={destination} />
        {canRenderDestinationFaq && (
          <>
            <SectionSeparator />
            <DestinationFAQ faqs={destinationFaq} titleSize={SectionTitleSizes.LARGE} />
          </>
        )}
      </ComponentContainer>
      <SectionSeparator />
      <Footer />
    </>
  );
};

export const getStaticProps: GetStaticProps = storeWrapper.getStaticProps((store) => async ({ params }: any) => {
  const data = await initializeApp(store, async () => {
    const path: string = getPlacePath(params);
    const countryPath = getCountryPath(path);
    const regionPath = getRegionPath(path);

    store.dispatch(setDestination(path));

    const contentApi = new ContentApi();

    const [countries, resortLanding, hero, popularHotelsResponse, { data: destinationFaq }] = await Promise.all([
      contentApi.getCountry(countryPath),
      contentApi.getResort(path),
      getHero({ path }),
      new HotelContentApi().tiles([path], 5),
      getDestinationFaqData(path)
    ]);

    const popularHotels = popularHotelsResponse?.data;
    const country = head(countries);
    const region: Region | undefined = findByPath(regionPath, country?.regions);
    const resort: Resort | undefined = findByPath(path, region?.resorts);

    return {
      path,
      destination: region?.isDestination ? region : country,
      region: region || null,
      resort: resort || null,
      resortLanding,
      hero,
      popularHotels: (popularHotels || []).map((hotel) => {
        const featuredImageUrl = hotel.featuredImage.url;
        return {
          ...hotel,
          gallery: truncateGallery(hotel.gallery, featuredImageUrl),
          place: {
            hotel: hotel.place.hotel
          }
        };
      }),
      destinationFaq: destinationFaq ?? [],
      promoBannerTripType: TripTypes.HOLIDAYS
    };
  });
  return {
    ...data,
    revalidate: Revalidate.HALF_HOUR
  };
});

export async function getStaticPaths() {
  const continents = await new ContentApi().getGeography();
  const regions = flatMap(flatMap(continents, 'countries'), 'regions');
  const resorts = flatMap(regions, 'resorts').filter(
    ({ tripTypes }) => !!tripTypes.find(({ tripType }) => tripType === TripTypes.HOLIDAYS)
  );
  const paths = resorts.map(({ path }) => Page.DESTINATION.concat('/').concat(path));

  return { paths, fallback: false };
}

export default withGlobalProvider(ResortPage);
